import { includes } from '../containers.ts';
import { assertAbsent, assertPresent, check, checkEqual, checkPresent } from '../core.ts';
import { isArray, isPresent, type KeysOf } from '../identity.ts';
import { pluralize } from '../strings.ts';
import type { DateTime } from '../time.ts';
import type { Quantity, Scalar, Unit } from '../units.ts';

export type DescriptorType =
  | 'bool'
  | 'number'
  | 'string'
  | 'datetime'
  | 'scalar'
  | 'dict'
  | 'ref'
  | 'array'
  | 'obj';

export interface BaseDescriptor {
  // k?: string;
  // <_T extends ValueType> = {
  t: DescriptorType;
  // Label prompt instead of key
  label?: string | undefined;
  // Is this property required?
  req?: boolean;
  // Is this property indexed for search?
  idx?: boolean | undefined;
  // Should this property be shown in summaries?
  show?: boolean | undefined;
  // Max width in characters in summaries.
  w?: number | undefined;
  // This property depends on this other key.
  dk?: string; // keyof Descriptor
  // This property depends on this other value.
  dv?: unknown; // typeof Descriptor[dk]
}

//
// Atomic types (non-aggregate)
//

export type AtomicValueType =
  | boolean
  | number
  | string
  | DateTime<true>
  | Scalar<Quantity>
  | RefId<Struct>;

export interface BooleanDescriptor extends BaseDescriptor {
  t: 'bool';
  // Optional icon for this value
  icon?: string;
}

export type BooleanTypeDescriptor<T = boolean> = T extends boolean ? BooleanDescriptor : never;

export interface NumberDescriptor<T extends number> extends BaseDescriptor {
  t: 'number';
  // If specified, the number must have one of these values.
  enum?: ReadonlyArray<T>;
  // Optional human-readable enum labels.
  enumLabels?: Record<T, string>;
}

// The idiom ([T] extends [x] ? Foo<T> ...) is to prevent Foo from distributing over a union type T.
export type NumberTypeDescriptor<T = number> = [T] extends [number] ? NumberDescriptor<T> : never;

export type StringSubType =
  | 'multi'
  | 'address'
  | 'email'
  | 'phone'
  | 'numeric'
  | 'url'
  | 'interval';

export interface StringDescriptor<T extends string> extends BaseDescriptor {
  t: 'string';
  // If specified, the string must have one of these values.
  // TODO: Consider requiring StringEnum<T> here.
  // enum?: ReadonlyArray<StringLiteral<T>>;
  enum?: ReadonlyArray<T>;
  // Optional human-readable enum labels.
  enumLabels?: Record<T, string>;
  // The semantic subtype for this value.
  st?: StringSubType | undefined;
}

// The idiom ([T] extends [x] ? Foo<T> ...) is to prevent Foo from distributing over a union type T.
export type StringTypeDescriptor<T = string> = [T] extends [string] ? StringDescriptor<T> : never;

export function hasEnumValue<T>(desc: { enum?: ReadonlyArray<T> }, value: unknown): value is T {
  return desc.enum?.includes(value as T) ?? false;
}

export type DateSubType =
  // A specific date at a specific time (default)
  | 'timestamp'
  // A specific date, time and timezone are ignored. See |standardDate|.
  | 'date'
  // A specific time, date and timezone are ignored. See |standardTime|.
  | 'time'
  // TODO: Fix these if they're still used.
  // // A specific date, although only year is mutable.
  | 'date-year';
// // A specific date, although only month + day is mutable.
// | 'date-calendar'
// // A month + day with no year.
// | 'calendar';

export interface DateDescriptor extends BaseDescriptor {
  t: 'datetime';
  // The semantic subtype of this value. Defaults to 'date-and-time'.
  st?: DateSubType | undefined;
}

export type DateTypeDescriptor<T = DateTime<true>> =
  T extends DateTime<true> ? DateDescriptor : never;

export interface ScalarDescriptor<Q extends Quantity> extends BaseDescriptor {
  t: 'scalar';
  u: Unit<Q>;
}

export type ScalarTypeDescriptor<T = Scalar<Quantity>> =
  T extends Scalar<infer Q> ? ScalarDescriptor<Q> : never;

export type RefId<_R> = {
  readonly path: string;
  readonly id: string;
};

export interface RefDescriptor<R extends Struct> extends BaseDescriptor {
  t: 'ref';
  // If you need to reference the type that this is a property of, use selfReference().
  desc: Descriptor<R>;
  // Keys from the referenced object to show in a summary.
  refKeys?: ReadonlyArray<keyof R> | undefined;
}

export type RefTypeOf<T> = T extends RefDescriptor<infer R> ? R : never;

export type RefTypeDescriptor<T = RefId<Struct>> =
  T extends RefId<infer R extends Struct> ? RefDescriptor<R> : never;

export type AtomicTypeDescriptor<T> =
  | BooleanTypeDescriptor<T>
  | NumberTypeDescriptor<T>
  | StringTypeDescriptor<T>
  | DateTypeDescriptor<T>
  | ScalarTypeDescriptor<T>
  | RefTypeDescriptor<T>;

//
// Simple types (non-recursive)
//

export interface SimpleStruct extends Partial<Record<string, DictValueType>> {}
export interface SimpleStructDescriptor<T extends SimpleStruct> extends BaseDescriptor {
  t: 'obj';
  el: StrictPropertyList<T>;
}

export type DictValueType = AtomicValueType | Struct; // SimpleStruct;
export type DictValueTypeDescriptor<DV> =
  DV extends RefId<Struct>
    ? RefTypeDescriptor<DV> & { req: true }
    : DV extends Struct // SimpleStruct
      ? StructDescriptor<DV> & { req: true }
      : // ? SimpleStructDescriptor<DV> & { req: true }
        AtomicTypeDescriptor<DV> & { req: true };

export interface Dict extends Partial<Record<string, DictValueType>> {}

export interface DictDescriptor<
  K extends string,
  T extends AtomicValueType | Struct, // SimpleStruct
> extends BaseDescriptor {
  t: 'dict';
  el: DictValueTypeDescriptor<T>;
  // If specified, the dict key must have one of these values.
  enum?: ReadonlyArray<K>;
}

export type DictTypeDescriptor<T = Partial<Record<string, DictValueType>>> =
  T extends Partial<Record<infer DK extends string, infer DV extends DictValueType>>
    ? DictDescriptor<DK, DV>
    : never;

export type SimpleValueType = AtomicValueType | Dict;
export type SimpleTypeDescriptor<T> = AtomicTypeDescriptor<T> | DictTypeDescriptor<T>;

//
// Recursive types
//

// export type Struct = Record<string, DerivedValueType | undefined>; // { [key: string]: DerivedValueType | undefined };
// export interface SimpleStruct extends Record<string, CompoundValueType | undefined> {}

// https://stackoverflow.com/a/70088091
export interface Struct extends Record<string, ValueType | undefined> {}
export type ElementType = SimpleValueType | Struct;
export type ValueType = ElementType | Array<ElementType>;

export interface ArrayDescriptor<E extends ElementType> extends BaseDescriptor {
  t: 'array';
  el: ElementTypeDescriptor<E> & { req: true };
}

export type ArrayTypeDescriptor<T = Array<ElementType>> =
  T extends Array<infer E extends ElementType> ? ArrayDescriptor<E> : never;

// export type PropertyDescriptor<T> = TypeDescriptor<T>;
// export type PropertyList<S extends Struct> = ReadonlyArray<
//   { k: keyof S & string } & TypeDescriptor<S[keyof S]>
// >;

export interface StructDescriptor<T extends Struct> extends BaseDescriptor {
  t: 'obj';
  el: StrictPropertyList<T>;
}

export type StructTypeDescriptor<T = Struct> = T extends Struct ? StructDescriptor<T> : never;

export type ElementTypeDescriptor<T> = SimpleTypeDescriptor<T> | StructTypeDescriptor<T>;
export type TypeDescriptor<T> = ElementTypeDescriptor<T> | ArrayTypeDescriptor<T>;
// export type TypeDescriptor<T = ValueType> =
//   | BooleanTypeDescriptor<T>
//   | NumberTypeDescriptor<T>
//   | StringTypeDescriptor<T>
//   | DateTypeDescriptor<T>
//   | ScalarTypeDescriptor<T>
//   | RefTypeDescriptor<T>
//   | DictTypeDescriptor<T>
//   | StructTypeDescriptor<T>
//   | ArrayTypeDescriptor<T>;

// We would like to distribute this over ValueType, but that doesn't work due to anti-spreading of
// number and string.
export type AnyTypeDescriptor = TypeDescriptor<any>;

// export type PropertyList<T> = ReadonlyArray<TypeDescriptor<T[keyof T]>>;

export type StrictPropertyDescriptorOf<T extends Struct> = {
  // The "& {}" clause ensures that we're describing the non-undefined portion of optional keys.
  [K in keyof T & string]: { k: K } & TypeDescriptor<T[K] & {}> &
    (undefined extends T[K] ? { req?: false } : { req: true });
}[keyof T & string];

export type StrictPropertyList<T extends Struct> = ReadonlyArray<StrictPropertyDescriptorOf<T>>;

// export type PropertyDescriptorOf<T> = {
//   [K in keyof T]: { k: K } & TypeDescriptor<T[K]>;
// }[keyof T];

export type AccessRole<CR extends string = never> =
  // Even logged out users have access.
  | 'public'
  // All users have this access.
  | 'users'
  // Only superusers and the user associated with this document (UID === path) has this access.
  | 'myself'
  // Only superusers have this access.
  | 'superuser'
  // No-one has access.
  | 'accessDenied'
  // Custom roles.
  | CR;

export type Access<CR extends string = never> = {
  // Whether the user can retrieve the data.
  read: AccessRole<CR>;
  // Whether the user "sees" the data in the UI.
  // if absent, defaults to |read|.
  enumerate?: AccessRole<CR>;
  // Whether the user can modify the data.
  write: AccessRole<CR>;
  // Whether the user can delete the data.
  // If absent, defaults to |write|.
  remove?: AccessRole<CR>;
};

export type ResolvedAccess<CR extends string = never> = Required<Access<CR>>;

export function resolve<CR extends string = never>(access: Access<CR>): ResolvedAccess<CR> {
  return {
    enumerate: access.read,
    remove: access.write,
    ...access,
  };
}

export type LabelKey<T> = keyof T | ((v: Partial<T>) => string | undefined);

type DescriptorMetadata<T, CR extends string = never> = {
  // Document type name.
  name: string | undefined;
  // Collection type name.
  plural: string | undefined;
  // The key to use as a label. If undefined, the id is the label.
  labelKey: LabelKey<T> | undefined;
  // Key on which to order results.
  orderBy: (keyof T & string) | undefined;
  // Who can view and/or modify documents in this collection.
  // N.B. This can be used in the client to steer the user away from data they can't interact with.
  // However, these rules are converted into Firebase rules to enforce access on the server.
  access: Access<CR>;
  // Whether this group collection can be queried as a group. This has implications for indices and
  // permissions. https://firebase.blog/posts/2019/06/understanding-collection-group-queries
  queryAsGroup: boolean;
};

export type RefsTo<T, R> = KeysOf<R, RefId<T>> & string;

export type RefPair<T, R extends {}> = [RefsTo<T, R>, Descriptor<R>];

// TODO: Consider having a way to express fields which may be present but should be treated as
// absent. Useful for deprecated fields in a database or denormalized data when importing.
export type Descriptor<T extends {}> = {
  readonly meta: DescriptorMetadata<T, string>;
  readonly props: StrictPropertyList<T>;
  // Sub-collections of this document.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly subs: ReadonlyArray<Descriptor<any>>;
  // Collections that can have a ref to these documents.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly exts: ReadonlyArray<RefPair<T, any>>;
};

export type MetaDescriptor<T extends {}, CR extends string = never> = Descriptor<T> & {
  readonly meta: DescriptorMetadata<T, CR>;
  readonly subs: ReadonlyArray<MetaDescriptor<any, CR>>;
};

export class Schema<T extends {}, CR extends string = never> implements MetaDescriptor<T, CR> {
  get props(): StrictPropertyList<T> {
    return checkPresent(this.props_);
  }
  get subs(): ReadonlyArray<Schema<any, CR>> {
    return [...this.subs_];
  }
  get exts(): ReadonlyArray<RefPair<T, any>> {
    return [...this.exts_];
  }

  protected constructor(public readonly meta: DescriptorMetadata<T, CR>) {}

  protected props_: StrictPropertyList<T> | undefined;
  protected subs_: Array<Schema<any, CR>> = [];
  protected exts_: Array<RefPair<T, any>> = [];
}

// TODO: Instead of subclassing, wrap. Then allow builder methods to accept Schema or SchemaBuilder.
export class SchemaBuilder<T extends {}, CR extends string = never> extends Schema<T, CR> {
  constructor(meta: DescriptorMetadata<T, CR>) {
    super(meta);
  }

  setProps(props: StrictPropertyList<T>): this {
    check(!this.built_);
    assertAbsent(this.props_);
    this.meta.labelKey ??= props.find((p) => includes(['name', 'label'], p.k as string))?.k;
    for (const p of props) {
      if (p.t === 'ref') {
        check(p.desc instanceof SchemaBuilder);
        p.desc.exts_.push([p.k, this as Descriptor<any>]);
      }
    }
    this.props_ = props;
    return this;
  }

  // TODO: Switch to using only setSubOf.
  addSub<U extends {}>(s: Schema<U, CR>): this {
    check(!this.subs_.includes(s));
    this.subs_.push(s);
    return this;
  }

  // Make this a constructor property.
  setSubOf<P extends {}>(p: Schema<P, CR>): this {
    check(!this.built_);
    check(p instanceof SchemaBuilder);
    p.addSub(this);
    return this;
  }

  build(): Schema<T, CR> {
    check(!this.built_);
    checkPresent(this.props_);
    // checkPresent(this.meta.labelKey);
    this.built_ = true;
    return this;
  }

  private built_ = false;
}

interface DescribeOptions<T, CR extends string = never> {
  name: string | undefined;
  plural?: string;
  labelKey?: LabelKey<T>;
  orderBy?: keyof T & string;
  access: Access<CR>;
  queryAsGroup?: boolean;
}

// const selfRefSymbol = Symbol('SelfReference');

const maxIdLength = 1000; // Actually 1500, but jeez.
const invalidIds: readonly string[] = ['.', '..', '__.*__'] as const;

//  https://firebase.google.com/docs/firestore/quotas#collections_documents_and_fields
export function sanitizeFirestoreId(
  id: string,
  options?: {
    maxLength?: number;
    // Disallowed, so a post-processing step must handle them.
    allowSlash?: boolean;
  },
) {
  const maxLength = Math.min(options?.maxLength ?? maxIdLength, maxIdLength);
  const allowSlash = options?.allowSlash ?? false;
  id = id.replaceAll('\\', '');
  if (!allowSlash) {
    id = id.replaceAll('/', '');
  }
  if (invalidIds.includes(id) || !id.length) {
    id = '_';
  }
  return id.slice(0, maxLength);
}

// export interface DescriptorBuilder<T extends Struct, CR extends string>
//   extends MetaDescriptor<T, CR> {
//   sub<U extends {}>(s: MetaDescriptor<U, CR>): this;
//   subOf<P extends {}>(p: MetaDescriptor<P, CR>): void;
// }

function checkCollName(name?: string) {
  if (isPresent(name)) {
    const sanitized = sanitizeFirestoreId(name);
    checkEqual(sanitized, name);
    // N.B. Spaces cause problems for typesense extension in emulator.
    check(!name.includes(' '));
  }
}

export function descriptorOf<T extends Struct, CR extends string = never>(
  options: DescribeOptions<T, CR>,
  props?: StrictPropertyList<T>,
): SchemaBuilder<T, CR> {
  checkCollName(options.name);
  const builder = new SchemaBuilder<T, CR>({
    name: options.name,
    plural: options.plural ?? (options.name ? pluralize(options.name) : undefined),
    labelKey: options.labelKey,
    orderBy: options.orderBy,
    access: options.access,
    queryAsGroup: options.queryAsGroup ?? false,
  });
  if (props) {
    builder.setProps(props);
  }
  return builder;
  // const result: DescriptorBuilder<T, CR> = {
  //   meta,
  //   props,
  //   subs: [],
  //   exts: [],
  //   sub(s) {
  //     this.subs.push(s);
  //     return this;
  //   },
  //   subOf(p) {
  //     p.subs.push(this as MetaDescriptor<any, CR>);
  //   },
  // };
  // for (const p of props) {
  //   if (p.t === 'ref') {
  //     const desc: Descriptor<any> = p.desc;
  //     if ((desc.meta.labelKey as string | symbol) === selfRefSymbol) {
  //       p.desc = result as Descriptor<any>;
  //     }
  //     (p.desc.exts as RefPair<T, any>[]).push([p.k, result as Descriptor<any>]);
  //   }
  // }
  // return result;
}

export function collName<T extends Struct>(desc: Descriptor<T>, overrideName?: string) {
  checkCollName(overrideName);
  const name = overrideName ?? desc.meta.name;
  assertPresent(name, 'A collection with a deferred name must provide an overrideName.');
  return name;
}

// This creates a pseudo-Descriptor that is only used for self-referential `RefId`s.
// export function selfReference<T extends {}>(): Descriptor<T> {
//   return {
//     meta: {
//       name: 'SelfReference',
//       plural: 'SelfReference',
//       labelKey: selfRefSymbol as keyof T,
//       orderBy: undefined,
//       access: {
//         read: 'superuser',
//         write: 'superuser',
//       },
//       queryAsGroup: false,
//     },
//     props: [],
//     subs: [],
//     exts: [],
//   };
// }

export type Snapshot<T> = {
  id: string;
  data(): T;
};

export function schemaKeys<T extends {}>(
  d: Descriptor<T> | StrictPropertyList<T>,
): Array<keyof T & string> {
  const props = isArray(d) ? d : d.props;
  return props.map((p) => p.k);
}

// Useful for standardizing APIs.
export type Doc<T> = { id: string; data: T };
